import React, { useEffect, useMemo, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ArrowUpRight, ChevronLeft, ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";
import DocumentationLayout from "../components/DocumentationLayout";
import CodeBlock from "../components/CodeBlock";
import { useAuthStore } from "../store/useAuthStore";

const slug = (text) =>
  text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
const parseArticles = (text) => {
  const articles = [];
  let fenced = false;
  text.split("\n").forEach((line) => {
    if (line.startsWith("```")) fenced = !fenced;
    if (!fenced && line.startsWith("## ")) {
      const title = line.slice(3).trim();
      articles.push({ title, id: slug(title), lines: [] });
    } else if (articles.length) articles[articles.length - 1].lines.push(line);
  });
  return articles
    .filter((article) => article.id !== "table-of-contents")
    .map((article) => ({
      ...article,
      body: article.lines.join("\n").replace(/\n---\s*$/, ""),
    }));
};

const introductions = {
  "getting-started":
    "Integrate WayaBank into your backend to collect customer payments through virtual accounts, send funds through payouts, issue cards and send SMS. Begin with your merchant credentials and a staging environment.",
  authentication:
    "Your backend exchanges merchant credentials for a Bearer token. Obtain a token before calling any product API, and keep credentials out of browser and mobile application code.",
  "virtual-account-api":
    "Use a static account for an ongoing customer relationship, or a dynamic account for a time-limited order or invoice. Store your reference alongside the returned account details so you can reconcile incoming payments.",
  "payout-api":
    "Start by identifying the beneficiary bank and verifying the account name. Submit the transfer with a unique reference, then use that reference to retrieve the transaction details for reconciliation. Payouts are sent from your merchant settlement account automatically.",
  "merchant-portal-settings":
    "Save your card encryption public key, card webhook URL and card balance URL before requesting cards. Settings are kept separately for TEST and LIVE.",
  "card-api":
    "Issue virtual and physical cards against your customers' static virtual accounts. You hold each customer's card balance, and WayaBank asks your card balance URL before approving every payment.",
  "sms-api":
    "Send OTPs, alerts and order updates to your customers' phones. Each SMS needs a unique reference, so a resend never delivers the same message twice.",
  webhooks:
    "Webhooks let your backend receive payment notifications without repeatedly checking for updates. Link incoming notifications to your records and handle repeated notifications without duplicating the business action.",
  "api-response-format":
    "Use the response body to understand the result of a request. Authentication and product endpoints use different response shapes; consult the example for the endpoint you are integrating.",
  "error-handling":
    "Use the HTTP status and response message together to diagnose a failed request. Keep transaction references in your logs so your team can investigate and reconcile requests.",
};

export default function DocumentationPage() {
  const { user } = useAuthStore();
  const [articles, setArticles] = useState([]);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(
    () => window.location.hash.slice(1) || "getting-started",
  );
  useEffect(() => {
    const controller = new AbortController();
    fetch("/docs/Documentation.md", { signal: controller.signal })
      .then((response) => {
        if (!response.ok) throw new Error("Unable to load documentation.");
        return response.text();
      })
      .then((text) => setArticles(parseArticles(text)))
      .catch((err) => {
        if (err.name !== "AbortError") setError(err.message);
      });
    return () => controller.abort();
  }, []);
  const article =
    articles.find(
      (item) =>
        item.id === selected ||
        item.lines.some(
          (line) => line.startsWith("### ") && slug(line.slice(4)) === selected,
        ),
    ) || articles[0];
  const toc = useMemo(() => {
    let fenced = false;
    return (article?.lines || []).flatMap((line) => {
      if (line.startsWith("```")) fenced = !fenced;
      return !fenced && line.startsWith("### ")
        ? [{ text: line.slice(4).trim(), id: slug(line.slice(4).trim()) }]
        : [];
    });
  }, [article]);
  useEffect(() => {
    if (article && selected !== article.id) {
      document.getElementById(selected)?.scrollIntoView();
    }
  }, [article, selected]);
  useEffect(() => {
    const onHash = () =>
      setSelected(window.location.hash.slice(1) || "getting-started");
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);
  const selectArticle = (id) => {
    setSelected(id);
    window.history.pushState(null, "", `#${id}`);
    window.scrollTo(0, 0);
  };
  const index = articles.indexOf(article);
  return (
    <DocumentationLayout
      articles={articles}
      activeId={article?.id}
      onSelect={selectArticle}
      toc={toc}
      portalLink={
        <Link
          className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary-dark"
          to={user ? "/dashboard" : "/login"}
        >
          {user ? "Dashboard" : "Merchant portal"}{" "}
          <ArrowUpRight size={16} aria-hidden="true" />
        </Link>
      }
    >
      {error ? (
        <div role="alert" className="rounded-xl border border-red-200 p-6">
          {error}{" "}
          <button
            className="underline"
            onClick={() => window.location.reload()}
          >
            Try again
          </button>
        </div>
      ) : !article ? (
        <p role="status">Loading documentation…</p>
      ) : (
        <>
          <p className="mb-4 text-sm font-semibold text-primary">
            Developer documentation / {article.title}
          </p>
          <h1 className="text-4xl font-bold tracking-tight text-slate-950">
            {article.title}
          </h1>
          {introductions[article.id] && (
            <p className="mt-5 text-lg leading-8 text-slate-600">
              {introductions[article.id]}
            </p>
          )}
          {article.id === "getting-started" && (
            <div className="my-8 rounded-xl border border-orange-200 bg-orange-50 p-5">
              <h2 className="font-semibold text-slate-900">
                Your integration journey
              </h2>
              <ol className="mt-3 list-decimal space-y-2 pl-5 text-sm leading-6 text-slate-700">
                <li>
                  Prepare your merchant account, credentials, IP whitelist, and
                  product subscriptions.
                </li>
                <li>
                  Authenticate from your backend to obtain an access token.
                </li>
                <li>
                  Create a virtual account to receive payments, or verify a
                  beneficiary and initiate a payout.
                </li>
                <li>
                  Connect payment notifications and retain references for
                  reconciliation.
                </li>
              </ol>
            </div>
          )}
          <div className="prose prose-slate mt-8 max-w-none prose-headings:scroll-mt-24 prose-headings:tracking-tight prose-a:text-primary prose-pre:max-w-full prose-th:text-left">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                pre: ({ children }) => (
                  <div className="my-6 overflow-x-auto rounded-xl bg-slate-950 text-sm">
                    {children}
                  </div>
                ),
                code: ({ className, children }) =>
                  className ? (
                    <CodeBlock className={className}>{children}</CodeBlock>
                  ) : (
                    <code>{children}</code>
                  ),
                h3: ({ children }) => (
                  <h3 id={slug(String(children))}>{children}</h3>
                ),
                table: ({ children }) => (
                  <div className="overflow-x-auto">
                    <table>{children}</table>
                  </div>
                ),
                a: ({ href, children }) => <a href={href}>{children}</a>,
              }}
            >
              {article.body}
            </ReactMarkdown>
          </div>
          <nav
            aria-label="Adjacent articles"
            className="mt-16 flex items-center justify-between gap-6 border-b border-slate-200 pb-12 pt-6"
          >
            {index > 0 ? (
              <button
                onClick={() => selectArticle(articles[index - 1].id)}
                className="group inline-flex items-center gap-3 text-left text-base font-semibold text-slate-700 transition-colors hover:text-primary sm:text-lg"
              >
                <ChevronLeft
                  size={20}
                  className="shrink-0 text-slate-400 group-hover:text-primary"
                  aria-hidden="true"
                />
                <span>{articles[index - 1].title}</span>
              </button>
            ) : (
              <span />
            )}
            {index < articles.length - 1 && (
              <button
                onClick={() => selectArticle(articles[index + 1].id)}
                className="group ml-auto inline-flex items-center gap-3 text-right text-base font-semibold text-slate-700 transition-colors hover:text-primary sm:text-lg"
              >
                <span>{articles[index + 1].title}</span>
                <ChevronRight
                  size={20}
                  className="shrink-0 text-slate-400 group-hover:text-primary"
                  aria-hidden="true"
                />
              </button>
            )}
          </nav>
        </>
      )}
    </DocumentationLayout>
  );
}
